import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib';

export interface PdfProgress {
  progress: number;
  currentPage: number;
  totalPages: number;
}

export interface PdfPageCount {
  generatedPdfPages: number;
  externalPdfPages: number[];
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private pdfBlob: Blob | null = null;
  private currentAbortController: AbortController | null = null;
  public progress$ = new Subject<PdfProgress>();
  public pageCount$ = new BehaviorSubject<PdfPageCount>({
    generatedPdfPages: 0,
    externalPdfPages: [],
    totalPages: 0,
  });

  async generatePDFWithOrientation(
    element: HTMLElement,
    externalPdfBytes: Uint8Array[],
    includeMetadata: boolean = false,
    orientation: 'portrait' | 'landscape' = 'portrait'
  ): Promise<void> {
    if (!element) {
      throw new Error('Element not found');
    }

    this.currentAbortController = new AbortController();
    const signal = this.currentAbortController.signal;

    try {
      const pdfPages = element.querySelectorAll('.pdf-page');
      const totalGeneratedPages = pdfPages.length;

      const pdfOrientation = orientation;
      const pageWidth = orientation === 'portrait' ? 210 : 297;
      const pageHeight = orientation === 'portrait' ? 297 : 210;

      const pdf = new jsPDF({
        orientation: pdfOrientation,
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pageTextContents: string[] = [];

      for (let i = 0; i < totalGeneratedPages; i++) {
        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const page = pdfPages[i] as HTMLElement;

        const tableViewers = page.getElementsByTagName(
          'app-pdf-page-table-viewer'
        );
        Array.from(tableViewers).forEach((viewer) => {
          const tables = viewer.getElementsByTagName('table');
          Array.from(tables).forEach((table) => {
            const cells = table.querySelectorAll('td, th');
            Array.from(cells).forEach((cell: any) => {
              cell.style.verticalAlign = 'top';
              cell.style.marginTop = '-4px';
              cell.style.paddingBottom = '10px';
              cell.style.paddingTop = '-2px';
            });
          });
        });

        const purpleHeaders = page.querySelectorAll(
          'div[style*="background-color:#A02C93"]'
        );
        Array.from(purpleHeaders).forEach((header) => {
          (header as HTMLElement).style.verticalAlign = 'top';
        });

        const pageText = includeMetadata ? this.extractTextContent(page) : '';
        if (includeMetadata) {
          pageTextContents.push(pageText);
        }

        const canvas = await html2canvas(page, {
          scale: 1.5,
          useCORS: true,
          allowTaint: false,
          logging: false,
          windowWidth: pageWidth * 3.7795275591,
          windowHeight: pageHeight * 3.7795275591,
          imageTimeout: 15000,
          removeContainer: true,
          backgroundColor: null,
          onclone: (document) => {
            const elements = document.querySelectorAll('.page-title');
            elements.forEach((el: any) => {
              if (el.style) {
                el.style.maxWidth = '100%';
                el.style.whiteSpace = 'nowrap';
                el.style.overflow = 'hidden';
                el.style.textOverflow = 'ellipsis';
                el.style.textAlign = 'center';
                el.style.margin = '0 auto';
                el.style.lineHeight = '2';
                el.style.height = '2em';
                el.style.display = 'block';
                el.style.marginBottom = '0';
              }
            });
          },
        });

        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        if (i > 0) {
          pdf.addPage();
        }
        pdf.addImage(
          imgData,
          'JPEG',
          0,
          0,
          pageWidth,
          pageHeight,
          undefined,
          'FAST'
        );

        if (includeMetadata) {
          pdf.setProperties({
            [`page${i + 1}Content`]: pageTextContents[i] || '',
          });
        }

        const progressData: PdfProgress = {
          progress:
            ((i + 1) / (totalGeneratedPages + externalPdfBytes.length)) * 50,
          currentPage: i + 1,
          totalPages: totalGeneratedPages + externalPdfBytes.length,
        };
        this.progress$.next(progressData);
      }

      if (signal.aborted) {
        throw new Error('PDF generation was cancelled');
      }

      const generatedPdfBytes = pdf.output('arraybuffer');

      const finalPdf = await PDFDocument.create();

      const generatedPdf = await PDFDocument.load(generatedPdfBytes);

      if (includeMetadata && pageTextContents.length > 0) {
        const metadata = {
          creator: 'SAFiRE PDF Generator',
          pages: pageTextContents.map((content, index) => ({
            page: index + 1,
            content: content || '',
          })),
        };
        finalPdf.setSubject(JSON.stringify(metadata));
      }

      const copiedPages = await finalPdf.copyPages(
        generatedPdf,
        generatedPdf.getPageIndices()
      );
      copiedPages.forEach((page: any) => finalPdf.addPage(page));

      let externalPageCount = 0;
      for (let i = 0; i < externalPdfBytes.length; i++) {
        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const pdfBytes = externalPdfBytes[i];
        const externalPdf = await PDFDocument.load(pdfBytes);
        const externalPages = await finalPdf.copyPages(
          externalPdf,
          externalPdf.getPageIndices()
        );

        for (const page of externalPages) {
          finalPdf.addPage(page);
          externalPageCount++;
        }

        const progressData: PdfProgress = {
          progress: 50 + ((i + 1) / externalPdfBytes.length) * 50,
          currentPage: totalGeneratedPages + i + 1,
          totalPages: totalGeneratedPages + externalPdfBytes.length,
        };
        this.progress$.next(progressData);
      }

      const finalPdfBytes = await finalPdf.save();
      this.pdfBlob = new Blob([new Uint8Array(finalPdfBytes)], {
        type: 'application/pdf',
      });

      const finalPageCount: PdfPageCount = {
        generatedPdfPages: totalGeneratedPages,
        externalPdfPages: [externalPageCount],
        totalPages: totalGeneratedPages + externalPageCount,
      };
      this.pageCount$.next(finalPageCount);
    } finally {
      this.currentAbortController = null;
    }
  }

  async generatePDF(
    element: HTMLElement,
    externalPdfBytes: Uint8Array[],
    includeMetadata: boolean = false
  ): Promise<void> {
    if (!element) {
      throw new Error('Element not found');
    }

    this.currentAbortController = new AbortController();
    const signal = this.currentAbortController.signal;

    try {
      const pdfPages = element.querySelectorAll('.pdf-page');
      const totalGeneratedPages = pdfPages.length;
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pageTextContents: string[] = [];

      for (let i = 0; i < totalGeneratedPages; i++) {
        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const page = pdfPages[i] as HTMLElement;

        const tableViewers = page.getElementsByTagName(
          'app-pdf-page-table-viewer'
        );
        Array.from(tableViewers).forEach((viewer) => {
          const tables = viewer.getElementsByTagName('table');
          Array.from(tables).forEach((table) => {
            const cells = table.querySelectorAll('td, th');
            Array.from(cells).forEach((cell: any) => {
              cell.style.verticalAlign = 'top';
              cell.style.marginTop = '-4px';
              cell.style.paddingBottom = '10px';
              cell.style.paddingTop = '-2px';
            });
          });
        });

        const purpleHeaders = page.querySelectorAll(
          'div[style*="background-color:#A02C93"]'
        );
        Array.from(purpleHeaders).forEach((header) => {
          (header as HTMLElement).style.verticalAlign = 'top';
        });

        const pageText = includeMetadata ? this.extractTextContent(page) : '';
        if (includeMetadata) {
          pageTextContents.push(pageText);
        }

        const canvas = await html2canvas(page, {
          scale: 1.5,
          useCORS: true,
          allowTaint: false,
          logging: false,
          windowWidth: 297 * 3.7795275591,
          windowHeight: 210 * 3.7795275591,
          imageTimeout: 15000,
          removeContainer: true,
          backgroundColor: null,
          onclone: (document) => {
            const elements = document.querySelectorAll('.page-title');
            elements.forEach((el: any) => {
              if (el.style) {
                el.style.maxWidth = '100%';
                el.style.whiteSpace = 'nowrap';
                el.style.overflow = 'hidden';
                el.style.textOverflow = 'ellipsis';
                el.style.textAlign = 'center';
                el.style.margin = '0 auto';
                el.style.lineHeight = '2';
                el.style.height = '2em';
                el.style.display = 'block';
                el.style.marginBottom = '0';
              }
            });
          },
        });

        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        if (i > 0) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210, undefined, 'FAST');

        if (includeMetadata) {
          pdf.setProperties({
            [`page${i + 1}Content`]: pageTextContents[i] || '',
          });
        }

        const progressData: PdfProgress = {
          progress:
            ((i + 1) / (totalGeneratedPages + externalPdfBytes.length)) * 50,
          currentPage: i + 1,
          totalPages: totalGeneratedPages + externalPdfBytes.length,
        };
        this.progress$.next(progressData);
      }

      if (signal.aborted) {
        throw new Error('PDF generation was cancelled');
      }

      // Convert the generated PDF to bytes
      const generatedPdfBytes = pdf.output('arraybuffer');

      // Create a new PDF document to hold the final result
      const finalPdf = await PDFDocument.create();

      // Load and copy the generated PDF pages
      const generatedPdf = await PDFDocument.load(generatedPdfBytes);

      // Set metadata in the final PDF if includeMetadata is true
      if (includeMetadata && pageTextContents.length > 0) {
        const metadata = {
          creator: 'SAFiRE PDF Generator',
          pages: pageTextContents.map((content, index) => ({
            page: index + 1,
            content: content || '',
          })),
        };
        finalPdf.setSubject(JSON.stringify(metadata));
      }

      const copiedPages = await finalPdf.copyPages(
        generatedPdf,
        generatedPdf.getPageIndices()
      );
      copiedPages.forEach((page: any) => finalPdf.addPage(page));

      // Add external PDFs
      let externalPageCount = 0;
      for (let i = 0; i < externalPdfBytes.length; i++) {
        if (signal.aborted) {
          throw new Error('PDF generation was cancelled');
        }

        const pdfBytes = externalPdfBytes[i];
        const externalPdf = await PDFDocument.load(pdfBytes);
        const externalPages = await finalPdf.copyPages(
          externalPdf,
          externalPdf.getPageIndices()
        );

        for (const page of externalPages) {
          finalPdf.addPage(page);
          externalPageCount++;
        }

        // Update progress for external PDFs
        const progressData: PdfProgress = {
          progress: 50 + ((i + 1) / externalPdfBytes.length) * 50,
          currentPage: totalGeneratedPages + i + 1,
          totalPages: totalGeneratedPages + externalPdfBytes.length,
        };
        this.progress$.next(progressData);
      }

      // Save the final PDF
      const finalPdfBytes = await finalPdf.save();
      this.pdfBlob = new Blob([new Uint8Array(finalPdfBytes)], {
        type: 'application/pdf',
      });

      // Update page count
      const finalPageCount: PdfPageCount = {
        generatedPdfPages: totalGeneratedPages,
        externalPdfPages: [externalPageCount],
        totalPages: totalGeneratedPages + externalPageCount,
      };
      this.pageCount$.next(finalPageCount);
    } finally {
      this.currentAbortController = null;
    }
  }

  private extractTextContent(element: HTMLElement): string {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;

        if (
          parent.style.display === 'none' ||
          parent.tagName.toLowerCase() === 'script' ||
          parent.tagName.toLowerCase() === 'style'
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let text = '';
    let currentNode = walker.nextNode();

    const processTable = (table: HTMLTableElement): string => {
      let tableText = '\n[Table]\n';
      const rows = table.rows;

      for (let i = 0; i < rows.length; i++) {
        const cells = rows[i].cells;
        const rowTexts = [];

        for (let j = 0; j < cells.length; j++) {
          const cellText = cells[j].textContent?.trim() || '';
          rowTexts.push(cellText);
        }

        tableText += rowTexts.join(' | ') + '\n';
      }

      return tableText + '[End Table]\n';
    };

    while (currentNode) {
      const parent = currentNode.parentElement;

      if (parent?.closest('table')) {
        currentNode = walker.nextNode();
        continue;
      }

      text += (currentNode.textContent || '').trim() + ' ';
      currentNode = walker.nextNode();
    }

    const tables = element.getElementsByTagName('table');
    for (const table of Array.from(tables)) {
      text += processTable(table);
    }

    const images = element.getElementsByTagName('img');
    for (const img of Array.from(images)) {
      const alt = img.getAttribute('alt');
      const title = img.getAttribute('title');
      if (alt || title) {
        text += `[Image: ${alt || title}] `;
      }
    }

    return text.trim();
  }

  private async getExternalPdfPageCounts(
    externalPdfBytes: Uint8Array[]
  ): Promise<number[]> {
    const pageCounts: number[] = [];
    for (const pdfBytes of externalPdfBytes) {
      const pdf = await PDFDocument.load(pdfBytes);
      pageCounts.push(pdf.getPageCount());
    }
    return pageCounts;
  }

  private async combinePDFs(
    generatedPdfBytes: ArrayBuffer,
    externalPdfBytesArray: Uint8Array[],
    pageTextContents: string[]
  ): Promise<Uint8Array> {
    try {
      const generatedPdf = await PDFDocument.load(generatedPdfBytes);

      if (pageTextContents.length > 0) {
        generatedPdf.setCreator('SAFiRE PDF Generator');

        const metadata = {
          creator: 'SAFiRE PDF Generator',
          pages: pageTextContents.map((content, index) => ({
            page: index + 1,
            content: content,
          })),
        };

        generatedPdf.setSubject(JSON.stringify(metadata));
      }

      // Load all external PDFs once and store them
      const externalPdfs = await Promise.all(
        externalPdfBytesArray.map((pdfBytes) => PDFDocument.load(pdfBytes))
      );

      const finalPdf = await PDFDocument.create();

      // Copy all pages from the generated PDF first
      const generatedPages = await finalPdf.copyPages(
        generatedPdf,
        generatedPdf.getPageIndices()
      );
      generatedPages.forEach((page: any) => finalPdf.addPage(page));

      const savedPdf = await finalPdf.save({
        useObjectStreams: false,
        addDefaultPage: false,
      });

      return savedPdf;
    } catch (error) {
      console.error('Error combining PDFs:', error);
      throw new Error('Failed to combine PDFs');
    }
  }

  private async getFinalPageCount(
    pdfBytes: Uint8Array,
    externalPdfBytes: Uint8Array[]
  ): Promise<PdfPageCount> {
    const pdf = await PDFDocument.load(pdfBytes);
    const totalPages = pdf.getPageCount();
    const externalPdfPages =
      await this.getExternalPdfPageCounts(externalPdfBytes);
    const generatedPdfPages =
      totalPages - externalPdfPages.reduce((sum, count) => sum + count, 0);

    return {
      generatedPdfPages,
      externalPdfPages,
      totalPages,
    };
  }

  getPdfBlob(): Blob | null {
    return this.pdfBlob;
  }

  openPdfInNewTab(filename: string): void {
    if (this.pdfBlob) {
      const pdfUrl = URL.createObjectURL(this.pdfBlob);
      const newTab = window.open(pdfUrl, '_blank');

      if (newTab) {
        const link = newTab.document.createElement('a');
        link.href = pdfUrl;
        link.download = filename;
        link.text = 'Download PDF';
        newTab.document.body.appendChild(link);
        link.click();

        newTab.onbeforeunload = () => {
          URL.revokeObjectURL(pdfUrl);
        };
      }
    }
  }

  downloadPdf(filename: string): void {
    if (this.pdfBlob) {
      const url = URL.createObjectURL(this.pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    }
  }

  private cleanup(): void {
    if (this.pdfBlob) {
      URL.revokeObjectURL(URL.createObjectURL(this.pdfBlob));
      this.pdfBlob = null;
    }
  }

  private async optimizePdfSize(pdfBytes: Uint8Array): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.load(pdfBytes);

    return await pdfDoc.save({
      useObjectStreams: true,
    });
  }

  public async verifyPdfMetadata(pdfBytes: Uint8Array): Promise<void> {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    // console.log('PDF Creator:', pdfDoc.getCreator());

    try {
      const metadata = JSON.parse(pdfDoc.getSubject() || '{}');
      // console.log('PDF Metadata:', metadata);

      if (metadata.pages) {
        metadata.pages.forEach((page: any) => {
          console.log(`\nPage ${page.page} Content:`, page.content);
        });
      }
    } catch (e) {
      console.log('No metadata found or invalid format');
    }
  }

  public cancelGeneration(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.cleanup();
    }
  }
}
